import { controlNameForPlaceholder } from '../forms/preload-controls.js'
import * as Types from '@aeppic/types'
import { buildDeferred, Deferred } from '@aeppic/shared/defer'
import { sleep } from '@aeppic/shared/sleep'

import { ControlLookup, ControlMatchStrategy } from './control-lookup.js'
import { isDocument } from '../../model/is.js'
import { Form } from '../../model/form.js'
import { Change } from '../../model/index.js'
import type { AeppicInterface } from '../aeppic.js'
import { LookupTrace } from './form-oriented-lookup.js'

export type FormRenderInfo = {
  formId: Types.DocumentId,
  version: string,
  strategy: 'full' | 'system' | 'default-only',
  refreshAt: string,
  placeholders: {
    [placeholderOffset: number]: string|null
  },
  traces?: LookupTrace[]
}

const FORMS_AFFECTING_CACHE = [
  'control-form',
  'application-form',
  'root-folder-form',
  'control-folder',
]

export class FormInfoRegistry {
  private _registry: Map<string, FormRenderInfo> = new Map()
  private _cacheClearCounter = 0

  public constructor(private _aeppic: AeppicInterface) {
  }

  clearCache() {
    this._registry.clear()
    this._cacheClearCounter++
  }

  get cacheClearCounter() {
    return this._cacheClearCounter
  }

  // Process a change and invalidate the cache if necessary
  async applyChangeForCacheInvalidation(change: Change) {
    let invalidate = false

    // Is the document a control ?
    if (FORMS_AFFECTING_CACHE.find(id => id == change.document.f.id)) {
      invalidate = true
    }

    if (invalidate) {
      this.clearCache()
    }
  }

  async getFormRenderInfo(formId: Types.DocumentId, version: string, strategy?: 'full' | 'system' | 'default-only', trace?: boolean): Promise<FormRenderInfo> {
    const key = JSON.stringify([formId, version, strategy ?? this._aeppic.Features.getOptionsFor('control-lookup')?.strategy ?? 'full'])
    const value = trace ? undefined : this._registry.get(key)
    
    if (value === undefined) {
      const info = await this.buildFormRenderInfo(formId, version, strategy, trace)
      if (!trace) this._registry.set(key, info)
      return info
    } else {
      return value
    }
  }

  async prebuild(delay?: number) {
    const forms = await this._aeppic.exportForms()

    const renderOptions = this._aeppic.Features.getOptionsFor('control-lookup')
    const strategy = renderOptions?.strategy ?? 'full'

    for (const exportedForm of forms) {
      const formDocument = isDocument(exportedForm) ? exportedForm : exportedForm.document
      const form = await this._aeppic.getForm({ id: formDocument.id, v: formDocument.v })
      const info = await this._buildFormRenderInfo(form, strategy)

      const key = JSON.stringify([formDocument.id, formDocument.v, strategy])
      this._registry.set(key, info)

      if (delay != null) {
        await sleep(delay)
      }
    }
  }

  async buildFormRenderInfo(formId: Types.DocumentId, version: string, strategy?: 'full' | 'system' | 'default-only', trace?: boolean) : Promise<FormRenderInfo> {
    const formDocument = await this._aeppic.getForm({ id: formId, v: version })

    if (!formDocument) {
      this._aeppic.Warn.once('form-info-registry', `missing-${formId}@${version}`, `Could not find form ${formId}@${version}`)
      return null
    }

    const info = await this._buildFormRenderInfo(formDocument, strategy, trace)
    return info
  }

  async _buildFormRenderInfo(form: Form, specifiedStrategy?: 'full' | 'system' | 'default-only', trace?: boolean): Promise<FormRenderInfo> {
    const strategy = specifiedStrategy ?? this._aeppic.Features.getOptionsFor('control-lookup')?.strategy ?? 'full'
    const lookup = new ControlLookup(this._aeppic, ControlMatchStrategy[strategy.toUpperCase().replace(/-/g, '_')] as unknown as ControlMatchStrategy)
    
    const formInfo: FormRenderInfo = {
      formId: form.id,
      version: form.v,
      strategy,
      refreshAt: new Date().toISOString(),
      placeholders: {},
      traces: trace ? [] : undefined
    }

    for (const placeholder of form.getPlaceholders()) {
      if (placeholder.field || placeholder.control) {
        const lookupTrace = trace ? new LookupTrace() : undefined
        
        if (trace) {
          formInfo.traces.push(lookupTrace)
        }

        const controlDocument = await lookup.findControlDocument(form.id, placeholder.field, controlNameForPlaceholder(placeholder), lookupTrace)
        formInfo.placeholders[placeholder.offset] = controlDocument?.id ?? null
      }
    }

    return formInfo
  }
}
